import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import { DashboardData, DashboardFilters, DashboardTab } from '../types/dashboard.types';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import { useTranslation } from '../../../i18n';
import { calculateTotalLegacyHours } from '../../../utils/legacyHoursUtils';
import { statusConfigFor } from './overviewConfig';

// Fleet-summary card + the per-drone grid (STD-014 split out of OverviewTab).
// Coupled to the parent only through the `onFilterChange` / `onTabChange`
// callbacks (selecting a drone switches to its profile tab); everything else is
// derived from the `data` prop.

type DronesSummary = DashboardData['allDronesSummary'];

interface DroneCardsSectionProps {
  serialNumbers: string[];
  dronesSummary: DronesSummary;
  onFilterChange: (filters: Partial<DashboardFilters>) => void;
  onTabChange: (tab: DashboardTab) => void;
}

export const DroneCardsSection: React.FC<DroneCardsSectionProps> = ({
  serialNumbers,
  dronesSummary,
  onFilterChange,
  onTabChange,
}) => {
  const { t } = useTranslation();

  // Data comes from props; there is no separate loading/error state here.
  const loading = false;
  const error = null;

  const formatEngineHours = (hours: number | null): string => {
    if (hours === null) return t('common.notAvailable');
    return hours.toFixed(1);
  };

  const formatMaintenanceHours = (
    nextHours: number | null,
    overdueHours: number | null
  ): { text: string; isOverdue: boolean } => {
    if (overdueHours !== null && overdueHours > 0) {
      return { text: t('overview.overdue', { hours: overdueHours.toFixed(1) }), isOverdue: true };
    } else if (nextHours !== null && nextHours >= 0) {
      return { text: t('overview.next', { hours: nextHours.toFixed(1) }), isOverdue: false };
    }
    return { text: t('common.notAvailable'), isOverdue: false };
  };

  const handleDroneClick = (serialNumber: string) => {
    // Select the drone
    onFilterChange({ selectedSerial: serialNumber });
    // Switch to profile tab
    onTabChange('profile');
  };

  return (
    <>
      {/* Fleet Summary Card */}
      <Card sx={{ mb: 3, backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.02) }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: BRAND_COLORS.forestGreen.main }}>
            {t('overview.fleetSummary')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                {t('overview.totalDrones')}
              </Typography>
              <Typography
                variant="h4"
                sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 600 }}
              >
                {serialNumbers.length}
              </Typography>
            </Grid>
            {dronesSummary && (
              <>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t('overview.totalEngineHours')}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 600 }}
                  >
                    {dronesSummary.drones
                      .reduce((total, drone) => {
                        const tracked = drone.total_engine_hours || 0;
                        const legacy = calculateTotalLegacyHours(drone.legacy_hours);
                        return total + tracked + legacy;
                      }, 0)
                      .toFixed(1)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t('overview.totalFlightHours')}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 600 }}
                  >
                    {dronesSummary.drones
                      .reduce((total, drone) => {
                        const tracked = drone.total_flight_hours || 0;
                        const legacy = calculateTotalLegacyHours(drone.legacy_hours);
                        return total + tracked + legacy;
                      }, 0)
                      .toFixed(1)}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* All Drones List */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="h5" sx={{ color: BRAND_COLORS.forestGreen.main }}>
          {t('overview.allDrones')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('overview.clickToViewProfile')}
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {dronesSummary && !loading && (
        <Grid container spacing={1.5}>
          {dronesSummary.drones.map((drone) => {
            // Use the actual drone status from database instead of custom logic
            const droneStatus = drone.status || 'Airworthy';
            const statusConfig = statusConfigFor(drone.status);

            // Get maintenance hours info
            const maintenanceHours = formatMaintenanceHours(
              drone.next_maintenance_hours,
              drone.overdue_hours
            );

            return (
              <Grid
                item
                xs={6}
                sm={4}
                md={3}
                lg={2}
                key={drone.serial_number}
                data-testid={`drone-card-${drone.serial_number}`}
              >
                <Card
                  onClick={() => handleDroneClick(drone.serial_number)}
                  sx={{
                    border: `1px solid ${statusConfig.color}`,
                    cursor: 'pointer',
                    position: 'relative',
                    height: '140px', // Fixed height for consistency
                    backgroundColor: statusConfig.bgColor,
                    '&:hover': {
                      border: `1px solid ${statusConfig.color}`,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: `0 4px 8px ${alpha(statusConfig.color, 0.2)}`,
                    },
                  }}
                >
                  {/* Status Badge - Show for all drones based on actual status */}
                  <Tooltip title={t(statusConfig.tooltipKey as Parameters<typeof t>[0])}>
                    <Chip
                      icon={<statusConfig.icon sx={{ fontSize: '12px !important' }} />}
                      label={
                        drone.organization_name && droneStatus === 'With customer'
                          ? drone.organization_name
                          : t(statusConfig.labelKey as Parameters<typeof t>[0])
                      }
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        height: '20px',
                        fontSize: '10px',
                        backgroundColor: statusConfig.color,
                        color: 'white',
                        fontWeight: 500,
                        '& .MuiChip-icon': {
                          color: 'white',
                          marginLeft: '2px',
                          marginRight: '-2px',
                        },
                        '& .MuiChip-label': {
                          paddingLeft: '4px',
                          paddingRight: '6px',
                        },
                      }}
                    />
                  </Tooltip>

                  <CardContent
                    sx={{
                      p: '12px !important', // Compact padding
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    {/* Header Row */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: BRAND_COLORS.forestGreen.main,
                            fontWeight: 600,
                            fontSize: '1rem',
                            lineHeight: 1.1,
                          }}
                        >
                          {t('overview.drone')} {drone.serial_number}
                        </Typography>
                        {drone.model && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', fontSize: '10px', lineHeight: 1.2 }}
                          >
                            {drone.model}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Main Content Layout - Left: Maintenance, Right: Hours */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 2,
                      }}
                    >
                      {/* Left Side - Maintenance Info */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '10px' }}
                        >
                          {t('overview.maintenance')}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: maintenanceHours.isOverdue
                              ? '#d32f2f'
                              : BRAND_COLORS.forestGreen.main,
                            fontWeight: 600,
                            lineHeight: 1.1,
                            fontSize: '1rem',
                          }}
                        >
                          {maintenanceHours.text}
                        </Typography>
                      </Box>

                      {/* Right Side - Engine & Flight Hours */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: 0.5,
                          minWidth: '80px',
                        }}
                      >
                        {/* Flight Hours (including legacy) */}
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '10px' }}
                          >
                            {t('overview.flightHours')}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: BRAND_COLORS.forestGreen.main,
                              fontWeight: 600,
                              lineHeight: 1.1,
                              fontSize: '0.875rem',
                            }}
                          >
                            {formatEngineHours(
                              (drone.total_flight_hours || 0) +
                                calculateTotalLegacyHours(drone.legacy_hours)
                            )}
                          </Typography>
                        </Box>

                        {/* Engine Hours (including legacy) */}
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '10px' }}
                          >
                            {t('overview.engineHours')}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: BRAND_COLORS.forestGreen.main,
                              fontWeight: 600,
                              lineHeight: 1.1,
                              fontSize: '0.875rem',
                            }}
                          >
                            {formatEngineHours(
                              (drone.total_engine_hours || 0) +
                                calculateTotalLegacyHours(drone.legacy_hours)
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {dronesSummary && dronesSummary.drones.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {t('overview.noDronesFound')}
          </Typography>
        </Box>
      )}
    </>
  );
};
